import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'

import {
  CopyOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  LinkOutlined,
} from '@ant-design/icons'

import {
  useEffect,
  useState,
} from 'react'

import {
  useSearchParams,
} from 'react-router-dom'

import { api } from 'services/api'
import LeadFormService from 'services/LeadFormService'


const {
  Title,
  Text,
} = Typography


const STATUS_COLORS = {
  ACTIVE: 'green',
  DRAFT: 'default',
  PAUSED: 'orange',
  FINISHED: 'blue',
  CANCELLED: 'red',
}


const SESSION_STATUS_COLORS = {
  WAITING: 'gold',
  CONFIRMED: 'cyan',
  STARTING: 'blue',
  PLAYING: 'green',
  FINISHED: 'purple',
  CANCELLED: 'red',
  EXPIRED: 'default',
}


const RiverRaceSettingsPage = () => {
  const [
    searchParams,
  ] = useSearchParams()


  const gameId =
    searchParams.get(
      'gameId',
    )


  // ======================================================
  // STATE
  // ======================================================

  const [
    game,
    setGame,
  ] = useState(null)


  const [
    loading,
    setLoading,
  ] = useState(true)


  const [
    creatingSession,
    setCreatingSession,
  ] = useState(false)


  const [
    session,
    setSession,
  ] = useState(null)


  const [
    stationName,
    setStationName,
  ] = useState(
    'TOTEM RIVER RACE 01',
  )


  const [
    sessionLoading,
    setSessionLoading,
  ] = useState(false)

  // ======================================================
  // RANKING
  // ======================================================

  const [ranking, setRanking] =
    useState([])

  const [loadingRanking, setLoadingRanking] =
    useState(false)

  const [rankingLimit, setRankingLimit] =
    useState(10)

  const [rankingDate, setRankingDate] =
    useState(null)

  const [
    clearingRanking,
    setClearingRanking,
  ] = useState(false)

  // ======================================================
  // LEAD DETAILS
  // ======================================================

  const [
    leadModalOpen,
    setLeadModalOpen,
  ] = useState(false)

  const [
    selectedLead,
    setSelectedLead,
  ] = useState(null)

  const [
    loadingLead,
    setLoadingLead,
  ] = useState(false)


  // ======================================================
  // CARREGAR GAME
  // ======================================================

  const loadGame =
    async () => {
      if (!gameId) {
        setLoading(false)

        return
      }


      setLoading(true)


      try {
        const response =
          await api.get(
            `/games/${gameId}`,
          )


        const data =
          response?.data ||
          response


        setGame(
          data,
        )
      } catch (error) {
        console.error(
          'ERRO LOAD RIVER RACE:',
          error,
        )


        message.error(
          'Erro ao carregar River Race',
        )


        setGame(null)
      } finally {
        setLoading(false)
      }
    }


  // ======================================================
  // CARREGAR RANKING
  // ======================================================

  const loadRanking =
    async (
      limit = rankingLimit,
      date = rankingDate,
    ) => {
      if (!gameId) {
        setRanking([])

        return
      }


      setLoadingRanking(true)


      try {
        const params = {}


        if (date) {
          params.date = date
        }


        const response =
          await api.get(
            `/game-sessions/river-race/${gameId}/ranking`,
            {
              params,
            },
          )


        const data =
          response?.data ||
          response


        const items =
          Array.isArray(
            data?.ranking,
          )
            ? data.ranking
            : []


        setRanking(
          items.slice(
            0,
            limit,
          ),
        )
      } catch (error) {
        console.error(
          'ERRO LOAD RIVER RACE RANKING:',
          error?.response?.data ||
          error,
        )


        message.error(
          error?.response?.data
            ?.message ||
          'Erro ao carregar ranking do River Race',
        )


        setRanking([])
      } finally {
        setLoadingRanking(false)
      }
    }

  // ======================================================
  // ZERAR RANKING
  // ======================================================

  const clearRanking =
    () => {
      if (!gameId) {
        return
      }


      Modal.confirm({
        title:
          'Zerar ranking do River Race?',

        content:
          'Todos os resultados deste jogo serão removidos. ' +
          'Os leads, participantes e cadastros serão mantidos.',

        okText:
          'Sim, zerar ranking',

        cancelText:
          'Cancelar',

        okType:
          'danger',

        centered:
          true,

        onOk:
          async () => {
            setClearingRanking(
              true,
            )


            try {
              const response =
                await api.delete(
                  `/game-sessions/river-race/${gameId}/ranking`,
                )


              const data =
                response?.data ||
                response


              message.success(
                data?.message ||
                'Ranking zerado com sucesso.',
              )


              // Limpa imediatamente a tabela.
              setRanking([])


              // Depois consulta novamente a API
              // para garantir sincronização.
              await loadRanking(
                rankingLimit,
                rankingDate,
              )
            }
            catch (error) {
              console.error(
                'ERRO CLEAR RIVER RACE RANKING:',
                error?.response?.data ||
                error,
              )


              message.error(
                error?.response?.data
                  ?.message ||
                'Erro ao zerar ranking do River Race.',
              )


              // Faz o Modal considerar a operação
              // como falha.
              throw error
            }
            finally {
              setClearingRanking(
                false,
              )
            }
          },
      })
    }


  // ======================================================
  // ABRIR LEAD
  // ======================================================

  const openLeadDetails =
    async (
      leadId,
    ) => {
      if (!leadId) {
        message.warning(
          'Este participante não possui lead vinculado.',
        )

        return
      }


      setLeadModalOpen(true)
      setSelectedLead(null)
      setLoadingLead(true)


      try {
        const response =
          await LeadFormService
            .getLead(
              leadId,
            )


        const data =
          response?.data ||
          response


        setSelectedLead(
          data,
        )
      }
      catch (error) {
        console.error(
          'ERRO LOAD LEAD:',
          error?.response?.data ||
          error,
        )


        message.error(
          error?.response?.data
            ?.message ||
          'Erro ao carregar os dados do lead.',
        )


        setLeadModalOpen(false)
      }
      finally {
        setLoadingLead(false)
      }
    }


  // ======================================================
  // CRIAR SESSÃO DE TESTE
  // ======================================================

  const createTestSession =
    async () => {
      if (!gameId) {
        return
      }


      setCreatingSession(true)


      try {
        const response =
          await api.post(
            '/game-sessions',
            {
              gameId,

              stationId:
                'RIVER-RACE-01',

              stationName:
                stationName ||
                'TOTEM RIVER RACE 01',
            },
          )


        const data =
          response?.data ||
          response


        setSession(
          data,
        )


        message.success(
          'Sessão criada com sucesso',
        )
      } catch (error) {
        console.error(
          'ERRO CREATE SESSION:',
          error?.response?.data ||
          error,
        )


        message.error(
          error?.response?.data
            ?.message ||
          'Erro ao criar sessão',
        )
      } finally {
        setCreatingSession(false)
      }
    }


  // ======================================================
  // CONSULTAR SESSÃO
  // ======================================================

  const refreshSession =
    async () => {
      if (
        !session?.sessionId
      ) {
        return
      }


      setSessionLoading(true)


      try {
        const response =
          await api.get(
            `/game-sessions/${session.sessionId}`,
          )


        const data =
          response?.data ||
          response


        setSession(
          (
            previous,
          ) => ({
            ...previous,
            ...data,
          }),
        )
      } catch (error) {
        console.error(
          'ERRO REFRESH SESSION:',
          error,
        )


        message.error(
          'Erro ao consultar sessão',
        )
      } finally {
        setSessionLoading(false)
      }
    }


  // ======================================================
  // COPY
  // ======================================================

  const copyText =
    async (
      value,
      successText,
    ) => {
      if (!value) {
        return
      }


      try {
        await navigator
          .clipboard
          .writeText(
            value,
          )


        message.success(
          successText ||
          'Copiado',
        )
      } catch (error) {
        console.error(
          'Erro copiando:',
          error,
        )


        message.error(
          'Não foi possível copiar',
        )
      }
    }


  // ======================================================
  // LOAD
  // ======================================================

  useEffect(
    () => {
      loadGame()
    },
    [
      gameId,
    ],
  )

  // ======================================================
  // LOAD RANKING
  // ======================================================

  useEffect(
    () => {
      if (!gameId) {
        setRanking([])

        return
      }


      loadRanking(
        rankingLimit,
        rankingDate,
      )
    },
    [
      gameId,
      rankingLimit,
      rankingDate,
    ],
  )


  // ======================================================
  // SEM GAME ID
  // ======================================================

  if (!gameId) {
    return (
      <Alert
        type="error"
        showIcon
        message="Game ID não informado"
        description="Abra esta tela através do botão Gerenciar na página Jogos."
      />
    )
  }


  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight:
            350,

          display:
            'flex',

          alignItems:
            'center',

          justifyContent:
            'center',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }


  // ======================================================
  // GAME NÃO ENCONTRADO
  // ======================================================

  if (!game) {
    return (
      <Alert
        type="error"
        showIcon
        message="River Race não encontrado"
      />
    )
  }

  // ======================================================
  // RANKING COLUMNS
  // ======================================================

  const rankingColumns = [
    {
      title:
        'Posição',

      dataIndex:
        'position',

      key:
        'position',

      width:
        110,

      align:
        'center',

      render: (
        position,
      ) => {
        if (position === 1) {
          return (
            <Tag color="gold">
              🥇 1º
            </Tag>
          )
        }


        if (position === 2) {
          return (
            <Tag color="default">
              🥈 2º
            </Tag>
          )
        }


        if (position === 3) {
          return (
            <Tag color="orange">
              🥉 3º
            </Tag>
          )
        }


        return (
          <Text strong>
            {position}º
          </Text>
        )
      },
    },


    {
      title:
        'Participante',

      dataIndex:
        'name',

      key:
        'name',

      render: (
        name,
        record,
      ) => (
        <Space
          direction="vertical"
          size={0}
        >
          <Text strong>
            {name || 'Participante'}
          </Text>

          {record.participantId && (
            <Text
              type="secondary"
              style={{
                fontSize:
                  11,
              }}
            >
              ID: {
                record.participantId
                  .slice(
                    0,
                    8,
                  )
              }...
            </Text>
          )}
        </Space>
      ),
    },


    {
      title:
        'Contato',

      key:
        'contact',

      width:
        260,

      render: (
        _,
        record,
      ) => (
        <Space
          direction="vertical"
          size={0}
        >
          <Text>
            {record.email || '—'}
          </Text>

          <Text type="secondary">
            {record.phone || '—'}
          </Text>
        </Space>
      ),
    },


    {
      title:
        'Pontuação',

      dataIndex:
        'score',

      key:
        'score',

      width:
        160,

      align:
        'right',

      render: (
        score,
      ) => (
        <Text
          strong
          style={{
            fontSize:
              18,
          }}
        >
          {Number(
            score ||
            0,
          ).toLocaleString(
            'pt-BR',
          )}
        </Text>
      ),
    },


    {
      title:
        'Lead',

      key:
        'lead',

      width:
        130,

      align:
        'center',

      render: (
        _,
        record,
      ) => {
        if (!record.leadId) {
          return (
            <Text type="secondary">
              —
            </Text>
          )
        }


        return (
          <Button
            size="small"
            type="link"
            icon={
              <LinkOutlined />
            }
            onClick={() =>
              openLeadDetails(
                record.leadId,
              )
            }
          >
            Ver lead
          </Button>
        )
      },
    },
  ]


  // ======================================================
  // COLUMNS
  // ======================================================

  const participantColumns = [
    {
      title:
        'Campo',

      dataIndex:
        'field',

      width:
        180,
    },

    {
      title:
        'Valor',

      dataIndex:
        'value',
    },
  ]


  const participantData =
    session?.participant
      ? [
        {
          key:
            'name',

          field:
            'Nome',

          value:
            session.participant
              .name ||
            '-',
        },

        {
          key:
            'email',

          field:
            'E-mail',

          value:
            session.participant
              .email ||
            '-',
        },

        {
          key:
            'phone',

          field:
            'Telefone',

          value:
            session.participant
              .phone ||
            '-',
        },
      ]
      : []


  // ======================================================
  // RENDER
  // ======================================================

  return (
    <>
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <Space
        style={{
          width:
            '100%',

          justifyContent:
            'space-between',

          marginBottom:
            20,
        }}
        align="start"
      >
        <div>
          <Title
            level={2}
            style={{
              margin:
                0,
            }}
          >
            River Race
          </Title>


          <Text type="secondary">
            Configuração e integração do jogo Unity
          </Text>
        </div>


        <Tag
          color={
            STATUS_COLORS[
            game.status
            ] ||
            'default'
          }
          style={{
            fontSize:
              14,

            padding:
              '5px 12px',
          }}
        >
          {game.status}
        </Tag>
      </Space>


      {/* ================================================= */}
      {/* GAME INFO */}
      {/* ================================================= */}

      <Card
        title="Informações do jogo"
        style={{
          marginBottom:
            20,
        }}
      >
        <Descriptions
          bordered
          column={{
            xs:
              1,

            sm:
              1,

            md:
              2,
          }}
        >
          <Descriptions.Item
            label="Nome"
          >
            {game.name}
          </Descriptions.Item>


          <Descriptions.Item
            label="Tipo"
          >
            <Tag color="cyan">
              RIVER RACE
            </Tag>
          </Descriptions.Item>


          <Descriptions.Item
            label="Evento"
          >
            {game.event?.name ||
              '-'}
          </Descriptions.Item>


          <Descriptions.Item
            label="Slug"
          >
            {game.slug}
          </Descriptions.Item>


          <Descriptions.Item
            label="Game ID"
            span={2}
          >
            <Space>
              <Text code>
                {game.id}
              </Text>


              <Button
                size="small"
                icon={
                  <CopyOutlined />
                }
                onClick={() =>
                  copyText(
                    game.id,
                    'Game ID copiado',
                  )
                }
              />
            </Space>
          </Descriptions.Item>
        </Descriptions>


        <Alert
          style={{
            marginTop:
              20,
          }}
          type="info"
          showIcon
          message="Game ID do Unity"
          description="Este UUID será configurado no GameStartFlowManager do Unity para que o totem crie sessões para este River Race."
        />
      </Card>


      {/* ================================================= */}
      {/* DASHBOARD */}
      {/* ================================================= */}

      <Row
        gutter={[
          16,
          16,
        ]}
        style={{
          marginBottom:
            20,
        }}
      >
        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card>
            <Statistic
              title="Sessões"
              value={
                session
                  ? 1
                  : 0
              }
            />
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card>
            <Statistic
              title="Participante atual"
              value={
                session
                  ?.participant
                  ? 1
                  : 0
              }
            />
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card>
            <Statistic
              title="Status"
              value={
                session?.status ||
                '—'
              }
            />
          </Card>
        </Col>


        <Col
          xs={24}
          sm={12}
          lg={6}
        >
          <Card>
            <Statistic
              title="Estação"
              value="01"
            />
          </Card>
        </Col>
      </Row>

      {/* ================================================= */}
      {/* RANKING */}
      {/* ================================================= */}

      <Card
        title="Ranking River Race"
        style={{
          marginBottom:
            20,
        }}
        extra={
          <Space wrap>
            <Select
              value={
                rankingLimit
              }
              style={{
                width:
                  120,
              }}
              onChange={
                setRankingLimit
              }
              options={[
                {
                  value:
                    10,

                  label:
                    'Top 10',
                },

                {
                  value:
                    20,

                  label:
                    'Top 20',
                },

                {
                  value:
                    50,

                  label:
                    'Top 50',
                },

                {
                  value:
                    100,

                  label:
                    'Top 100',
                },
              ]}
            />


            <Button
              icon={
                <ReloadOutlined />
              }
              loading={
                loadingRanking
              }
              onClick={() =>
                loadRanking(
                  rankingLimit,
                  rankingDate,
                )
              }
            >
              Atualizar
            </Button>
            <Button
              danger
              loading={
                clearingRanking
              }
              disabled={
                loadingRanking
              }
              onClick={
                clearRanking
              }
            >
              Zerar ranking
            </Button>
          </Space>
        }
      >
        <Space
          direction="vertical"
          size={16}
          style={{
            width:
              '100%',
          }}
        >
          {/* ============================================= */}
          {/* FILTRO */}
          {/* ============================================= */}

          <Space wrap>
            <Button
              type={
                rankingDate ===
                  null
                  ? 'primary'
                  : 'default'
              }
              onClick={() =>
                setRankingDate(
                  null,
                )
              }
            >
              Ranking Geral
            </Button>


            <Button
              type={
                rankingDate !==
                  null
                  ? 'primary'
                  : 'default'
              }
              onClick={() => {
                const today =
                  new Date()

                const year =
                  today
                    .getFullYear()

                const month =
                  String(
                    today
                      .getMonth() +
                    1,
                  )
                    .padStart(
                      2,
                      '0',
                    )

                const day =
                  String(
                    today
                      .getDate(),
                  )
                    .padStart(
                      2,
                      '0',
                    )

                setRankingDate(
                  `${year}-${month}-${day}`,
                )
              }}
            >
              Ranking de Hoje
            </Button>


            <Input
              type="date"
              value={
                rankingDate ||
                ''
              }
              onChange={(
                event,
              ) =>
                setRankingDate(
                  event
                    .target
                    .value ||
                  null,
                )
              }
              style={{
                width:
                  170,
              }}
            />
          </Space>


          {/* ============================================= */}
          {/* INFORMAÇÃO */}
          {/* ============================================= */}

          <Alert
            type="info"
            showIcon
            message={
              rankingDate
                ? `Ranking do dia ${rankingDate
                  .split('-')
                  .reverse()
                  .join('/')}`
                : 'Ranking geral'
            }
            description={
              rankingDate
                ? 'Exibindo a melhor pontuação de cada participante neste dia.'
                : 'Exibindo a melhor pontuação registrada de cada participante.'
            }
          />


          {/* ============================================= */}
          {/* TABELA */}
          {/* ============================================= */}

          <Table
            rowKey={
              (
                record,
              ) =>
                record.participantId ||
                `${record.position}-${record.name}`
            }
            columns={
              rankingColumns
            }
            dataSource={
              ranking
            }
            loading={
              loadingRanking
            }
            pagination={
              false
            }
            locale={{
              emptyText:
                'Nenhum resultado registrado no ranking.',
            }}
          />
        </Space>
      </Card>


      {/* ================================================= */}
      {/* TESTE DE SESSÃO */}
      {/* ================================================= */}

      <Card
        title="Teste da integração Unity → Tablet"
        style={{
          marginBottom:
            20,
        }}
      >
        <Alert
          type="info"
          showIcon
          message="Sessão de teste"
          description="Use esta função para testar o cadastro do tablet antes de conectar o Unity."
          style={{
            marginBottom:
              20,
          }}
        />


        <Row
          gutter={[
            16,
            16,
          ]}
        >
          <Col
            xs={24}
            md={14}
          >
            <Text strong>
              Nome da estação
            </Text>


            <Input
              value={
                stationName
              }
              onChange={
                (
                  event,
                ) =>
                  setStationName(
                    event
                      .target
                      .value,
                  )
              }
              style={{
                marginTop:
                  8,
              }}
              placeholder="TOTEM RIVER RACE 01"
            />
          </Col>


          <Col
            xs={24}
            md={10}
            style={{
              display:
                'flex',

              alignItems:
                'flex-end',
            }}
          >
            <Button
              type="primary"
              icon={
                <ExperimentOutlined />
              }
              loading={
                creatingSession
              }
              onClick={
                createTestSession
              }
              block
            >
              Criar Sessão de Teste
            </Button>
          </Col>
        </Row>


        {/* =============================================== */}
        {/* SESSION */}
        {/* =============================================== */}

        {
          session && (
            <>
              <Divider />


              <Space
                style={{
                  width:
                    '100%',

                  justifyContent:
                    'space-between',

                  marginBottom:
                    16,
                }}
              >
                <Title
                  level={4}
                  style={{
                    margin:
                      0,
                  }}
                >
                  Sessão atual
                </Title>


                <Button
                  icon={
                    <ReloadOutlined />
                  }
                  loading={
                    sessionLoading
                  }
                  onClick={
                    refreshSession
                  }
                >
                  Atualizar
                </Button>
              </Space>


              <Descriptions
                bordered
                column={1}
              >
                <Descriptions.Item
                  label="Session ID"
                >
                  <Space>
                    <Text code>
                      {
                        session.sessionId
                      }
                    </Text>


                    <Button
                      size="small"
                      icon={
                        <CopyOutlined />
                      }
                      onClick={() =>
                        copyText(
                          session.sessionId,
                          'Session ID copiado',
                        )
                      }
                    />
                  </Space>
                </Descriptions.Item>


                <Descriptions.Item
                  label="Status"
                >
                  <Tag
                    color={
                      SESSION_STATUS_COLORS[
                      session.status
                      ] ||
                      'default'
                    }
                  >
                    {
                      session.status
                    }
                  </Tag>
                </Descriptions.Item>


                {
                  session.publicToken && (
                    <Descriptions.Item
                      label="Public Token"
                    >
                      <Text code>
                        {
                          session.publicToken
                        }
                      </Text>
                    </Descriptions.Item>
                  )
                }


                {
                  session.registrationUrl && (
                    <Descriptions.Item
                      label="Cadastro"
                    >
                      <Space wrap>
                        <Text
                          style={{
                            wordBreak:
                              'break-all',
                          }}
                        >
                          {
                            session.registrationUrl
                          }
                        </Text>


                        <Button
                          size="small"
                          icon={
                            <LinkOutlined />
                          }
                          onClick={() =>
                            window.open(
                              session.registrationUrl,
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          Abrir
                        </Button>


                        <Button
                          size="small"
                          icon={
                            <CopyOutlined />
                          }
                          onClick={() =>
                            copyText(
                              session.registrationUrl,
                              'Link copiado',
                            )
                          }
                        >
                          Copiar
                        </Button>
                      </Space>
                    </Descriptions.Item>
                  )
                }
              </Descriptions>


              {/* ========================================= */}
              {/* PARTICIPANTE */}
              {/* ========================================= */}

              {
                session.participant && (
                  <>
                    <Divider />


                    <Title level={4}>
                      Participante confirmado
                    </Title>


                    <Table
                      rowKey="key"
                      pagination={
                        false
                      }
                      size="small"
                      columns={
                        participantColumns
                      }
                      dataSource={
                        participantData
                      }
                    />
                  </>
                )
              }
            </>
          )
        }
      </Card>
            {/* ================================================= */}
      {/* DETALHES DO LEAD */}
      {/* ================================================= */}

      <Modal
        title="Dados do participante"
        open={leadModalOpen}
        onCancel={() => {
          setLeadModalOpen(false)
          setSelectedLead(null)
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setLeadModalOpen(false)
              setSelectedLead(null)
            }}
          >
            Fechar
          </Button>,
        ]}
        width={700}
        centered
      >
        {loadingLead ? (
          <div
            style={{
              minHeight: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : selectedLead ? (
          <Space
            direction="vertical"
            size={16}
            style={{
              width: '100%',
            }}
          >
            <Descriptions
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="Lead ID">
                <Text copyable>
                  {selectedLead.id}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Data do cadastro">
                {selectedLead.createdAt
                  ? new Date(
                      selectedLead.createdAt,
                    ).toLocaleString(
                      'pt-BR',
                    )
                  : '—'}
              </Descriptions.Item>

              <Descriptions.Item label="Formulário">
                {selectedLead.form?.name ||
                  selectedLead.form?.title ||
                  '—'}
              </Descriptions.Item>
            </Descriptions>


            {Array.isArray(
              selectedLead.values,
            ) &&
              selectedLead.values.length >
                0 && (
                <>
                  <Divider
                    style={{
                      margin:
                        '4px 0',
                    }}
                  />

                  <Title
                    level={5}
                    style={{
                      margin: 0,
                    }}
                  >
                    Dados cadastrados
                  </Title>

                  <Descriptions
                    bordered
                    column={1}
                    size="small"
                  >
                    {selectedLead.values.map(
                      (
                        item,
                        index,
                      ) => (
                        <Descriptions.Item
                          key={
                            item.id ||
                            index
                          }
                          label={
                            item.field
                              ?.label ||
                            item.field
                              ?.fieldKey ||
                            `Campo ${
                              index + 1
                            }`
                          }
                        >
                          {item.value ??
                            '—'}
                        </Descriptions.Item>
                      ),
                    )}
                  </Descriptions>
                </>
              )}
          </Space>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Lead não encontrado"
          />
        )}
      </Modal>
    </>
  )
}


export default RiverRaceSettingsPage